import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { apiGet, apiPost, setAuthToken, clearAuthToken } from '../services/api';
import { msalInstance, loginRequest, isSSOConfigured } from '../config/msalConfig';
import { useTeams } from './TeamsContext';

const AuthContext = createContext(null);

// Detect if running inside an iframe (Teams loads tabs in iframes)
let isInIframe = false;
try {
  isInIframe = window.self !== window.top;
} catch {
  // Cross-origin iframe — assume we're in an iframe
  isInIframe = true;
}

// Shared promise so initialize() is called exactly once
let msalInitPromise = null;
function ensureMsalInitialized() {
  if (!msalInitPromise) {
    msalInitPromise = msalInstance.initialize();
  }
  return msalInitPromise;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { isInTeams, teamsReady } = useTeams();

  useEffect(() => {
    if (!teamsReady) return;

    // Inside Teams: use Teams SSO
    if (isInTeams) {
      attemptTeamsSSOLogin();
      return;
    }

    if (!isSSOConfigured) {
      restoreSession();
      return;
    }

    // Outside Teams with SSO configured: use redirect flow (only works outside iframes)
    ensureMsalInitialized().then(async () => {
      // Handle redirect return
      if (!isInIframe) {
        try {
          const response = await msalInstance.handleRedirectPromise();
          if (response && response.accessToken) {
            msalInstance.setActiveAccount(response.account);
            await exchangeToken(response);
            return;
          }
        } catch (err) {
          console.error('MSAL redirect handling error:', err?.message || err);
        }
      }

      // Check for existing MSAL session
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        msalInstance.setActiveAccount(accounts[0]);
        try {
          const response = await msalInstance.acquireTokenSilent({
            ...loginRequest,
            account: accounts[0],
          });
          await exchangeToken(response);
          return;
        } catch (err) {
          console.error('Silent token acquisition failed:', err?.message || err);
        }
      }

      // No MSAL session — try restoring from httpOnly cookie via /auth/me
      restoreSession();
    }).catch((err) => {
      console.error('MSAL initialization error:', err?.message || err);
      restoreSession();
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamsReady, isInTeams]);

  const attemptTeamsSSOLogin = async () => {
    try {
      const teams = await import('@microsoft/teams-js');
      const token = await teams.authentication.getAuthToken();
      const data = await apiPost('/auth/sso', {
        access_token: token,
        id_token: token,
        account: { name: '', username: '' },
        source: 'teams',
      });
      // Server sets httpOnly cookies. Also set Bearer fallback for Teams cross-origin.
      if (data.token) {
        setAuthToken(data.token);
      }
      setUser(data.user);
      setLoading(false);
      return;
    } catch (err) {
      console.warn('Teams silent SSO failed:', err?.message || err);
      // Teams SSO failed — try existing session cookie before showing login
      try {
        const data = await apiGet('/auth/me');
        setUser(data);
        setLoading(false);
        return;
      } catch {
        // No valid session — show login page (user can click "Sign in with Microsoft")
      }

      // Don't set a blocking error — let the user try the manual sign-in button
      // which uses teams.authentication.authenticate() (Teams-managed dialog)
      setLoading(false);
    }
  };

  // Try to restore session from httpOnly cookie (call /auth/me)
  const restoreSession = () => {
    apiGet('/auth/me')
      .then(data => { setUser(data); setLoading(false); })
      .catch(() => {
        clearAuthToken();
        setLoading(false);
      });
  };

  const exchangeToken = async (msalResponse) => {
    try {
      const data = await apiPost('/auth/sso', {
        access_token: msalResponse.accessToken,
        id_token: msalResponse.idToken,
        account: {
          name: msalResponse.account.name,
          username: msalResponse.account.username,
        },
      });
      // Server sets httpOnly cookies. Also store Bearer fallback.
      if (data.token) {
        setAuthToken(data.token);
      }
      setUser(data.user);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.response?.data?.message || err.message || 'SSO login failed';
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  // SSO login: Teams auth dialog when in Teams, redirect when outside
  const loginSSO = useCallback(async () => {
    setError(null);
    try {
      if (isInTeams) {
        // In Teams: use Teams-managed auth dialog (browser popups are blocked)
        const teams = await import('@microsoft/teams-js');
        const resultStr = await teams.authentication.authenticate({
          url: `${window.location.origin}/auth-popup`,
          width: 600,
          height: 535,
        });
        // TeamsAuthPopup.js calls notifySuccess with JSON payload
        const result = JSON.parse(resultStr);
        await exchangeToken({
          accessToken: result.accessToken,
          idToken: result.idToken,
          account: result.account,
        });
      } else {
        await ensureMsalInitialized();
        if (isInIframe) {
          // Non-Teams iframe: try popup (rare case)
          const response = await msalInstance.loginPopup(loginRequest);
          if (response && response.accessToken) {
            msalInstance.setActiveAccount(response.account);
            await exchangeToken(response);
          }
        } else {
          await msalInstance.loginRedirect(loginRequest);
        }
      }
    } catch (err) {
      setError(err.message || 'SSO login failed');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInTeams]);

  const logout = useCallback(async () => {
    // Call server logout to revoke refresh token + clear cookies
    try {
      await apiPost('/auth/logout');
    } catch {
      // Best effort
    }
    clearAuthToken();
    setUser(null);
    if (isSSOConfigured && msalInstance.getActiveAccount()) {
      try {
        await ensureMsalInitialized();
        if (isInIframe) {
          msalInstance.clearCache();
        } else {
          await msalInstance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
        }
      } catch (err) {
        console.error('Logout error:', err?.message || err);
      }
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const data = await apiGet('/auth/me');
      setUser(data);
    } catch (err) {
      console.error('User refresh failed:', err?.message || err);
    }
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      error,
      login: loginSSO,
      loginSSO,
      logout,
      refreshUser,
      isAdmin: user?.role === 'admin',
      isSSOConfigured,
      isInTeams,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
